import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import { Collapse, List, ListItemButton } from '@mui/material';
import { GeofenceStatus } from 'interfaces/awd.interface';

export type Orientation = 'vertical' | 'horizontal';

export interface PolygonSelectorProps {
	activeItemIndex?: number;
	approvedAccentPrimaryColor?: string;
	approvedAccentSecondaryColor?: string;
	defaultAccentColor?: string;
	isOpen?: boolean;
	orientation?: Orientation;
	listItems: any[];
	onItemClick?: (a: any) => any;
	rejectedAccentPrimaryColor?: string;
	rejectedAccentSecondaryColor?: string;
	toggleCollapse: (a: any) => any;
}

const verticalOrientationStyles = {
	maxHeight: 400,
	overflowY: 'scroll',
};

const horizontalOrientationStyles = {
	maxWidth: 400,
	overflowX: 'scroll',
};

const PolygonSelector = ({
	activeItemIndex = undefined,
	approvedAccentPrimaryColor = '#66B429',
	approvedAccentSecondaryColor = '#72e31b',
	defaultAccentColor = 'black',
	isOpen = false,
	listItems,
	onItemClick,
	orientation = 'vertical',
	rejectedAccentPrimaryColor = '#D10000',
	rejectedAccentSecondaryColor = '#f71c0c',
	toggleCollapse,
}: PolygonSelectorProps): JSX.Element => {
	const handleItemClick = ({ index }: { index: number }) => {
		if (onItemClick !== undefined) {
			onItemClick({
				index: index - 1,
			});
		}
	};

	const getPrimaryBackgroundColor = (
		activeItemIndex: number | undefined,
		currentItemIndex: number,
		polygonApprovalStatus: GeofenceStatus,
	) => {
		if (activeItemIndex === currentItemIndex) {
			return getSecondaryBackgroundColor(polygonApprovalStatus);
		}
		return polygonApprovalStatus === 'GEOFENCING_SAVED'
			? approvedAccentPrimaryColor
			: polygonApprovalStatus === 'GEOFENCING_REJECTED'
			? rejectedAccentPrimaryColor
			: 'white';
	};

	const getSecondaryBackgroundColor = (
		polygonApprovalStatus: GeofenceStatus,
	) => {
		return polygonApprovalStatus === 'GEOFENCING_SAVED'
			? approvedAccentSecondaryColor
			: polygonApprovalStatus === 'GEOFENCING_REJECTED'
			? rejectedAccentSecondaryColor
			: '#dadada';
	};

	return (
		<div
			style={{
				height: '100%',
				display: 'flex',
			}}
		>
			<Collapse
				in={isOpen}
				timeout="auto"
				unmountOnExit
				orientation={orientation}
			>
				<List
					component="div"
					disablePadding
					sx={{
						...(orientation === 'horizontal'
							? horizontalOrientationStyles
							: verticalOrientationStyles),
						borderRadius: '2px',
						height: 40,
						display:
							orientation === 'horizontal' ? 'flex' : 'block',
						'&::-webkit-scrollbar': {
							display: 'none',
						},
					}}
				>
					{listItems.map((item, index) => (
						<ListItemButton
							key={index + 1}
							sx={{
								background: getPrimaryBackgroundColor(
									activeItemIndex,
									index,
									item.polygonApprovalStatus,
								),
								border: '1px solid',
								borderWidth: activeItemIndex === index ? 2 : 0,
								borderColor:
									activeItemIndex === index
										? 'rgba(0, 153, 223, 1)'
										: 'transparent',
								borderRadius: '2px',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								marginRight: '2px',
								padding: '0px',
								minHeight: '40px',
								minWidth: '40px',
								'&:hover': {
									background: getSecondaryBackgroundColor(
										item.polygonApprovalStatus,
									),
								},
							}}
							onClick={() =>
								handleItemClick({
									index: index + 1,
								})
							}
						>
							<span
								style={{
									fontWeight: 'bold',
									color:
										item.polygonApprovalStatus ===
										'GEOFENCING_PENDING'
											? defaultAccentColor
											: 'white',
								}}
							>
								{index + 1}
							</span>
						</ListItemButton>
					))}
				</List>
			</Collapse>
			<ListItemButton
				onClick={toggleCollapse}
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: '0px',
					border: '1px solid',
					borderColor: 'transparent',
					background: 'white',
					borderRadius: '2px',
					height: '40px',
					width: '40px',
					...(orientation === 'horizontal'
						? { transform: 'rotate(-90deg)' }
						: {}),
					'&:hover': { background: '#dadada' },
				}}
			>
				{isOpen ? <ExpandMore /> : <ExpandLess />}
			</ListItemButton>
		</div>
	);
};

export default PolygonSelector;
